/**
 * Testimonial Slider JavaScript
 * Warehouse Theme - Shopify
 */

class TestimonialSlider {
  constructor(element) {
    this.slider = element;
    this.sliderWrapper = this.slider.closest('.testimonial-slider-wrapper');
    this.slides = this.slider.querySelectorAll('.testimonial-slide');
    this.prevBtn = this.sliderWrapper?.querySelector('.slider-arrow-prev');
    this.nextBtn = this.sliderWrapper?.querySelector('.slider-arrow-next');
    this.dotsContainer = this.slider.closest('.testimonial-slider-section')?.querySelector('.testimonial-dots');
    
    // Settings
    this.autoplay = this.slider.dataset.autoplay === 'true';
    this.autoplaySpeed = parseInt(this.slider.dataset.autoplaySpeed) || 5000;
    this.slideSpeed = parseInt(this.slider.dataset.slideSpeed) || 800;
    this.slidesToShow = parseInt(this.slider.dataset.slidesToShow) || 2;
    
    // State
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.isAnimating = false;
    this.autoplayTimer = null;
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    // Breakpoints
    this.breakpoints = {
      mobile: 576,
      tablet: 992,
      desktop: 1200
    };
    
    this.init();
  }
  
  init() {
    if (this.totalSlides === 0) return;
    
    this.updateSlidesToShow();
    this.createDots();
    this.bindEvents();
    this.updateSlider(false);
    
    if (this.autoplay) {
      this.startAutoplay();
    }
    
    this.animateInitialSlides();
  }
  
  animateInitialSlides() {
    this.slides.forEach((slide, index) => {
      slide.style.animationDelay = `${index * 0.1}s`;
    });
  }
  
  updateSlidesToShow() {
    const windowWidth = window.innerWidth;
    
    if (windowWidth < this.breakpoints.mobile) {
      this.slidesToShow = 1;
    } else if (windowWidth < this.breakpoints.tablet) {
      this.slidesToShow = 1;
    } else if (windowWidth < this.breakpoints.desktop) {
      this.slidesToShow = Math.min(2, parseInt(this.slider.dataset.slidesToShow) || 2);
    } else {
      this.slidesToShow = parseInt(this.slider.dataset.slidesToShow) || 2;
    }
    
    this.updateSlideWidths();
  }
  
  updateSlideWidths() {
    const slideWidth = `0 0 calc(80% - 1.2rem)`;
    this.slides.forEach(slide => {
      slide.style.flex = slideWidth;
    });
  }
  
  createDots() {
    if (!this.dotsContainer) return;
    
    this.dotsContainer.innerHTML = '';
    const totalDots = Math.ceil(this.totalSlides - this.slidesToShow + 1);
    
    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.className = 'testimonial-dot';
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('data-index', i);
      dot.type = 'button';
      
      if (i === 0) {
        dot.classList.add('active');
      }
      
      this.dotsContainer.appendChild(dot);
    }
  }
  
  bindEvents() {
    // Arrow navigation
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prevSlide());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.nextSlide());
    }
    
    // Dot navigation
    if (this.dotsContainer) {
      this.dotsContainer.addEventListener('click', (e) => {
        if (e.target.classList.contains('testimonial-dot')) {
          const index = parseInt(e.target.dataset.index);
          this.goToSlide(index);
        }
      });
    }
    
    // Keyboard navigation
    this.slider.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (e.key === 'ArrowRight') {
        this.nextSlide();
      }
    });
    
    // Touch events
    this.slider.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.slider.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: true });
    this.slider.addEventListener('touchend', () => this.handleTouchEnd());
    
    // Mouse drag events
    this.slider.addEventListener('mousedown', (e) => this.handleMouseDown(e));
    this.slider.addEventListener('mousemove', (e) => this.handleMouseMove(e));
    this.slider.addEventListener('mouseup', () => this.handleMouseUp());
    this.slider.addEventListener('mouseleave', () => this.handleMouseUp());
    
    // Pause on hover
    if (this.autoplay) {
      const section = this.slider.closest('.testimonial-slider-section');
      if (section) {
        section.addEventListener('mouseenter', () => this.stopAutoplay());
        section.addEventListener('mouseleave', () => this.startAutoplay());
      }
    }
    
    // Window resize
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.updateSlidesToShow();
        this.createDots();
        this.updateSlider(false);
      }, 250);
    });
    
    // Visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.stopAutoplay();
      } else if (this.autoplay) {
        this.startAutoplay();
      }
    });
  }
  
  handleTouchStart(e) {
    this.touchStartX = e.touches[0].clientX;
    this.stopAutoplay();
  }
  
  handleTouchMove(e) {
    this.touchEndX = e.touches[0].clientX;
  }
  
  handleTouchEnd() {
    const diff = this.touchStartX - this.touchEndX;
    const threshold = 50;
    
    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
    
    if (this.autoplay) {
      this.startAutoplay();
    }
  }
  
  handleMouseDown(e) {
    this.isDragging = true;
    this.startX = e.pageX;
    this.prevTranslate = this.currentTranslate;
    this.slider.style.cursor = 'grabbing';
    this.stopAutoplay();
  }
  
  handleMouseMove(e) {
    if (!this.isDragging) return;
    
    const currentX = e.pageX;
    const diff = currentX - this.startX;
    const maxDrag = 100;
    
    const limitedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff));
    this.currentTranslate = this.prevTranslate + limitedDiff;
  }
  
  handleMouseUp() {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.slider.style.cursor = 'grab';
    
    const diff = this.currentTranslate - this.prevTranslate;
    const threshold = 30;
    
    if (Math.abs(diff) > threshold) {
      if (diff < 0) {
        this.nextSlide();
      } else {
        this.prevSlide();
      }
    }
    
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    
    if (this.autoplay) {
      this.startAutoplay();
    }
  }
  
  prevSlide() {
    if (this.isAnimating) return;
    
    const newIndex = this.currentIndex - 1;
    if (newIndex >= 0) {
      this.goToSlide(newIndex);
    }
  }
  
  nextSlide() {
    if (this.isAnimating) return;
    
    const maxIndex = this.totalSlides - this.slidesToShow;
    const newIndex = this.currentIndex + 1;
    
    if (newIndex <= maxIndex) {
      this.goToSlide(newIndex);
    }
  }
  
  goToSlide(index, animate = true) {
    if (this.isAnimating) return;
    
    const maxIndex = this.totalSlides - this.slidesToShow;
    this.currentIndex = Math.max(0, Math.min(index, maxIndex));
    this.updateSlider(animate);
    this.resetAutoplay();
  }
  
  updateSlider(animate = true) {
    if (animate) {
      this.isAnimating = true;
    }
    
    // Calculate translate distance
    const firstSlide = this.slides[0];
    const slideWidth = firstSlide ? firstSlide.offsetWidth + parseFloat(getComputedStyle(firstSlide).marginRight) : 0;
    const translateX = -(this.currentIndex * slideWidth);
    
    // Apply transition
    if (animate) {
      this.slider.style.transition = `transform ${this.slideSpeed}ms cubic-bezier(0.4, 0, 0.2, 1)`;
      this.slider.style.transform = `translateX(${translateX}px)`;
      
      setTimeout(() => {
        this.isAnimating = false;
      }, this.slideSpeed);
    } else {
      this.slider.style.transition = 'none';
      this.slider.style.transform = `translateX(${translateX}px)`;
    }
    
    this.updateArrows();
    this.updateDots();
    this.announceSlide();
  }
  
  updateArrows() {
    const maxIndex = this.totalSlides - this.slidesToShow;
    
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      this.nextBtn.disabled = this.currentIndex >= maxIndex;
    }
  }
  
  updateDots() {
    if (!this.dotsContainer) return;
    
    const dots = this.dotsContainer.querySelectorAll('.testimonial-dot');
    dots.forEach(dot => dot.classList.remove('active'));
    
    const activeDot = this.dotsContainer.querySelector(`.testimonial-dot[data-index="${this.currentIndex}"]`);
    if (activeDot) {
      activeDot.classList.add('active');
    }
  }
  
  announceSlide() {
    let liveRegion = document.getElementById('testimonial-live-region');
    if (!liveRegion) {
      liveRegion = document.createElement('div');
      liveRegion.id = 'testimonial-live-region';
      liveRegion.className = 'sr-only';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'true');
      document.body.appendChild(liveRegion);
    }
    
    const total = Math.ceil(this.totalSlides - this.slidesToShow + 1);
    liveRegion.textContent = `Slide ${this.currentIndex + 1} of ${total}`;
  }
  
  startAutoplay() {
    if (!this.autoplay) return;
    
    this.stopAutoplay();
    
    this.autoplayTimer = setInterval(() => {
      const maxIndex = this.totalSlides - this.slidesToShow;
      
      if (this.currentIndex >= maxIndex) {
        this.goToSlide(0);
      } else {
        this.nextSlide();
      }
    }, this.autoplaySpeed);
  }
  
  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }
  
  resetAutoplay() {
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }
  
  destroy() {
    this.stopAutoplay();
    // Remove event listeners would go here
  }
}

// Initialize sliders
if (!customElements.get('testimonial-slider')) {
  customElements.define('testimonial-slider', class extends HTMLElement {
    connectedCallback() {
      const sliderElement = this.querySelector('.testimonial-slider');
      if (sliderElement && !sliderElement.testimonialSlider) {
        sliderElement.testimonialSlider = new TestimonialSlider(sliderElement);
      }
    }
  });
}

// Initialize for dynamic sections (theme editor)
document.addEventListener('DOMContentLoaded', () => {
  const sliders = document.querySelectorAll('.testimonial-slider');
  sliders.forEach(slider => {
    if (!slider.testimonialSlider) {
      slider.testimonialSlider = new TestimonialSlider(slider);
    }
  });
});

// Shopify theme editor support
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const slider = event.target.querySelector('.testimonial-slider');
    if (slider && !slider.testimonialSlider) {
      slider.testimonialSlider = new TestimonialSlider(slider);
    }
  });
  
  document.addEventListener('shopify:section:unload', (event) => {
    const slider = event.target.querySelector('.testimonial-slider');
    if (slider && slider.testimonialSlider) {
      slider.testimonialSlider.destroy();
      slider.testimonialSlider = null;
    }
  });
  
  document.addEventListener('shopify:section:select', (event) => {
    const slider = event.target.querySelector('.testimonial-slider');
    if (slider && slider.testimonialSlider) {
      slider.testimonialSlider.stopAutoplay();
    }
  });
  
  document.addEventListener('shopify:section:deselect', (event) => {
    const slider = event.target.querySelector('.testimonial-slider');
    if (slider && slider.testimonialSlider && slider.testimonialSlider.autoplay) {
      slider.testimonialSlider.startAutoplay();
    }
  });
}
